/**
 * Keyboard-safe layout (t207u). One hook for every mobile surface with a text input: follow the on-screen keyboard
 * with visualViewport (resize + scroll) and report the inset (innerHeight - vv.height - vv.offsetTop) plus the visible
 * box. Sheets and dialogs pin their root to that box, so their scroll area shrinks and a pinned composer sits right on
 * the keyboard. The maths is shared (keyboardInset) for React Native parity. Only the phone shell marks html `kb-open`
 * (useKeyboard), so a sheet unmounting can't clear it while the shell still has the keyboard up.
 */
import { useEffect, useState, type RefObject } from 'react';
import { keyboardInset } from '@tradeoutlet/shared';

export interface KeyboardState { inset: number; height: number; top: number }
const CLOSED: KeyboardState = { inset: 0, height: 0, top: 0 };

/** How long iOS takes to finish its keyboard animation before we nudge the focused field into view. */
export const KEYBOARD_SETTLE_MS = 300;

export function useKeyboardInset(): KeyboardState {
  const [kb, setKb] = useState<KeyboardState>(CLOSED);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const on = () => {
      const inset = keyboardInset(window.innerHeight, vv.height, vv.offsetTop);
      setKb(prev => (prev.inset === inset && prev.height === vv.height && prev.top === vv.offsetTop ? prev : inset ? { inset, height: vv.height, top: vv.offsetTop } : CLOSED));
    };
    on();
    vv.addEventListener('resize', on);
    vv.addEventListener('scroll', on);
    return () => { vv.removeEventListener('resize', on); vv.removeEventListener('scroll', on); };
  }, []);
  return kb;
}

/** Root style that pins a fixed overlay to the visible area while the keyboard is open (undefined when closed). */
export function keyboardBox(kb: KeyboardState): { top: number; height: number; bottom: 'auto' } | undefined {
  return kb.inset ? { top: kb.top, height: kb.height, bottom: 'auto' } : undefined;
}

/**
 * Once the keyboard has opened, bring the focused field inside `root` into view with block 'nearest' (never 'start',
 * which makes iOS fling the whole sheet up). Runs on focus too, for a second field focused while the keyboard is up.
 */
export function useKeepFocusedVisible(root: RefObject<HTMLElement | null>, kb: KeyboardState, active = true) {
  useEffect(() => {
    if (!active) return;
    const nudge = () => {
      const el = document.activeElement as HTMLElement | null;
      if (el && root.current?.contains(el) && el.matches('input, textarea, select, [contenteditable="true"]')) { el.scrollIntoView({ block: 'nearest' }); clearStickyFooter(el, root.current); }
    };
    const t = kb.inset ? setTimeout(nudge, KEYBOARD_SETTLE_MS) : undefined;
    const r = root.current;
    const onFocus = () => setTimeout(nudge, KEYBOARD_SETTLE_MS);
    r?.addEventListener('focusin', onFocus);
    return () => { if (t) clearTimeout(t); r?.removeEventListener('focusin', onFocus); };
  }, [root, kb.inset, kb.height, active]);
}

/**
 * scrollIntoView 'nearest' ignores a sticky footer (a Post button bar at bottom-0), so a field can land behind it. Scroll
 * the field's scroller on by the overlap with any bottom-sticky element in the same sheet (t207u keyboard check).
 */
export function clearStickyFooter(el: HTMLElement, root: HTMLElement) {
  const field = el.getBoundingClientRect();
  let cover = Infinity;
  root.querySelectorAll<HTMLElement>('[class*="sticky"]').forEach(s => {
    if (s.contains(el)) return;
    const cs = getComputedStyle(s);
    if (cs.position !== 'sticky' || cs.bottom === 'auto') return;
    cover = Math.min(cover, s.getBoundingClientRect().top);
  });
  const overlap = field.bottom - cover;
  if (!(overlap > 0)) return;
  let sc: HTMLElement | null = el.parentElement;
  while (sc && sc !== root.parentElement && !(sc.scrollHeight > sc.clientHeight && /auto|scroll/.test(getComputedStyle(sc).overflowY))) sc = sc.parentElement;
  if (sc) sc.scrollTop += Math.ceil(overlap) + 8;
}

/** The phone shell's hook: the inset plus the html `kb-open` class (hides the tab bar, drops home-indicator padding). */
export function useKeyboard(): KeyboardState {
  const kb = useKeyboardInset();
  useEffect(() => {
    document.documentElement.classList.toggle('kb-open', kb.inset > 0);
    return () => document.documentElement.classList.remove('kb-open');
  }, [kb.inset]);
  return kb;
}
